(function () {
  if (window.__dcodedCartPromoInit) return;
  window.__dcodedCartPromoInit = true;

  const getPromoFromNode = (node) => node?.closest?.('dcoded-cart-promo') || null;

  let activePromo = null;

  const getActivePromo = () => {
    if (activePromo?.isConnected) return activePromo;
    activePromo = document.querySelector('cart-drawer dcoded-cart-promo') || document.querySelector('dcoded-cart-promo');
    return activePromo;
  };

  const PROMO_OUTSIDE_CLICK_SELECTOR = [
    '[data-promo-sheet]',
    '[data-promo-overlay]',
    '[data-promo-form]',
    '[data-promo-apply]',
    '[data-tap-code]',
    '[data-promo-close]',
    '.dcoded-promo__input'
  ].join(',');

  const isPromoOutsideClickTarget = (target) => Boolean(target?.closest?.(PROMO_OUTSIDE_CLICK_SELECTOR));

  const patchCartDrawerOutsideClick = () => {
    const CartDrawer = customElements.get('cart-drawer');
    if (!CartDrawer || CartDrawer.prototype._dcodedPromoPatched) return;

    const originalHide = CartDrawer.prototype.hideForOutsideClickTarget;
    CartDrawer.prototype.hideForOutsideClickTarget = function (target) {
      if (isPromoOutsideClickTarget(target)) return false;
      return originalHide.call(this, target);
    };

    const originalAllow = CartDrawer.prototype.allowOutsideClickForTarget;
    CartDrawer.prototype.allowOutsideClickForTarget = function (target) {
      if (isPromoOutsideClickTarget(target)) return true;
      return originalAllow.call(this, target);
    };

    CartDrawer.prototype._dcodedPromoPatched = true;
  };

  patchCartDrawerOutsideClick();
  customElements.whenDefined('cart-drawer').then(patchCartDrawerOutsideClick);

  const bindCartDrawerLifecycle = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer._dcodedPromoLifecycleBound) return;
    drawer._dcodedPromoLifecycleBound = true;

    drawer.addEventListener('dialog:before-hide', () => {
      drawer.querySelectorAll('dcoded-cart-promo').forEach((promo) => promo._forceCloseSheet());
      activePromo = null;
    });

    drawer.addEventListener('dialog:after-show', () => {
      activePromo = null;
      drawer.querySelectorAll('dcoded-cart-promo').forEach((promo) => promo._reclaimSheetNodes());
    });
  };

  const onCartDrawerRefreshed = () => {
    bindCartDrawerLifecycle();
    document.querySelectorAll('cart-drawer dcoded-cart-promo').forEach((promo) => promo._reclaimSheetNodes());
  };

  bindCartDrawerLifecycle();
  customElements.whenDefined('cart-drawer').then(bindCartDrawerLifecycle);
  document.addEventListener('cart-drawer:refreshed', onCartDrawerRefreshed);

  const formatMoney = (cents) => {
    const format = window.themeVariables?.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(cents / 100))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100))
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  };

  const normalizeCode = (value) => (value || '').trim().toLowerCase();

  class DcodedCartPromo extends HTMLElement {
    disconnectedCallback() {
      this._forceCloseSheet();
      this._overlay?.remove();
      this._sheet?.remove();
      this._overlay = null;
      this._sheet = null;
      this._portaled = false;
      if (activePromo === this) activePromo = null;
    }

    _resetSheetRefs() {
      this._overlay = null;
      this._sheet = null;
      this._portaled = false;
    }

    _bindPortaledNodes(overlay, sheet) {
      overlay._dcodedPromo = this;
      sheet._dcodedPromo = this;
    }

    _restoreSheetToHost() {
      if (this._overlay && this._overlay.parentElement !== this) {
        this.appendChild(this._overlay);
      }
      if (this._sheet && this._sheet.parentElement !== this) {
        this.appendChild(this._sheet);
      }
      this._portaled = false;
    }

    _reclaimSheetNodes() {
      const hostOverlay = this.querySelector('[data-promo-overlay]');
      const hostSheet = this.querySelector('[data-promo-sheet]');

      if (hostOverlay) this._overlay = hostOverlay;
      if (hostSheet) this._sheet = hostSheet;

      if (!this._overlay) {
        const bodyOverlay = Array.from(document.querySelectorAll('[data-promo-overlay]')).find(
          (el) => el._dcodedPromo === this
        );
        if (bodyOverlay) {
          this._overlay = bodyOverlay;
          this.appendChild(bodyOverlay);
        }
      }

      if (!this._sheet) {
        const bodySheet = Array.from(document.querySelectorAll('[data-promo-sheet]')).find(
          (el) => el._dcodedPromo === this
        );
        if (bodySheet) {
          this._sheet = bodySheet;
          this.appendChild(bodySheet);
        }
      }

      if (this._overlay && this._sheet) {
        this._bindPortaledNodes(this._overlay, this._sheet);
      }

      this._portaled = false;
      this._overlay?.classList.remove('dcoded-promo__overlay--visible');
      this._sheet?.classList.remove('dcoded-promo__sheet--open');
    }

    _forceCloseSheet() {
      this._unbindReposition();
      this._overlay?.classList.remove('dcoded-promo__overlay--visible');
      this._sheet?.classList.remove('dcoded-promo__sheet--open');
      this._restoreSheetToHost();
      if (activePromo === this) activePromo = null;
    }

    _getCartDrawer() {
      return this.closest('cart-drawer') || document.querySelector('cart-drawer');
    }

    _getDrawerPanelRect() {
      const drawer = this._getCartDrawer();
      if (!drawer) return null;

      const panel = typeof drawer.getShadowPartByName === 'function'
        ? drawer.getShadowPartByName('content')
        : drawer.shadowRoot?.querySelector('[part="content"]');

      return (panel || drawer).getBoundingClientRect();
    }

    _positionInDrawer() {
      if (!this._overlay || !this._sheet) return;

      const rect = this._getDrawerPanelRect();
      if (!rect) return;
      const bottomOffset = Math.max(0, window.innerHeight - rect.bottom);

      Object.assign(this._overlay.style, {
        top: `${rect.top}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        right: 'auto',
        bottom: 'auto'
      });

      Object.assign(this._sheet.style, {
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        bottom: `${bottomOffset}px`,
        right: 'auto',
        maxHeight: `${Math.min(rect.height * 0.75, 420)}px`
      });
    }

    _bindReposition() {
      if (this._repositionHandler) return;
      this._repositionHandler = () => {
        if (this._sheet?.classList.contains('dcoded-promo__sheet--open')) {
          this._positionInDrawer();
        }
      };
      window.addEventListener('resize', this._repositionHandler);
      window.addEventListener('scroll', this._repositionHandler, true);
    }

    _unbindReposition() {
      if (!this._repositionHandler) return;
      window.removeEventListener('resize', this._repositionHandler);
      window.removeEventListener('scroll', this._repositionHandler, true);
      this._repositionHandler = null;
    }

    connectedCallback() {
      this._sectionId = this.dataset.sectionId || '';

      if (this.dataset.appliedCode && sessionStorage.getItem('dcoded-promo-just-applied')) {
        sessionStorage.removeItem('dcoded-promo-just-applied');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this._playApplyAnimation());
        });
      }
    }

    _shouldAnimate() {
      return this.dataset.showAnimation !== 'false';
    }

    static _markJustApplied() {
      sessionStorage.setItem('dcoded-promo-just-applied', '1');
    }

    _playApplyAnimation() {
      if (!this._shouldAnimate()) return;

      const pill = this.querySelector('.dcoded-promo__trigger--applied');
      if (pill) {
        pill.classList.add('dcoded-promo__trigger--celebrate');
        setTimeout(() => pill.classList.remove('dcoded-promo__trigger--celebrate'), 700);
      }

      const confettiWrap = this.querySelector('[data-promo-confetti]');
      if (confettiWrap) this._burstConfetti(confettiWrap);
    }

    _renderEligibleCodes() {
      const codesStr = (this.dataset.eligibleCodes || '').trim();
      const sheet = this._sheet || this.querySelector('[data-promo-sheet]');
      if (!sheet) return;

      let section = sheet.querySelector('[data-promo-eligible-section]');
      if (!section) {
        section = document.createElement('div');
        section.className = 'dcoded-promo__eligible';
        section.setAttribute('data-promo-eligible-section', '');
        section.innerHTML = '<p class="dcoded-promo__eligible-heading"></p><ul class="dcoded-promo__eligible-list" data-promo-eligible-list></ul>';
        sheet.querySelector('.dcoded-promo__sheet-body')?.appendChild(section);
      }

      const heading = section.querySelector('.dcoded-promo__eligible-heading');
      const list = section.querySelector('[data-promo-eligible-list]');
      if (!list) return;

      if (!codesStr) {
        section.hidden = true;
        list.innerHTML = '';
        return;
      }

      if (heading) {
        heading.textContent = this.dataset.eligibleHeading || 'ELIGIBLE FOR YOU';
      }

      list.innerHTML = '';
      codesStr.split('||').forEach((entry) => {
        const splitAt = entry.indexOf('::');
        const code = (splitAt === -1 ? entry : entry.slice(0, splitAt)).trim();
        const desc = (splitAt === -1 ? '' : entry.slice(splitAt + 2)).trim();
        if (!code) return;

        const li = document.createElement('li');
        li.className = 'dcoded-promo__eligible-item';
        li.dataset.eligibleCode = code;

        const info = document.createElement('div');
        info.className = 'dcoded-promo__eligible-info';

        const codeEl = document.createElement('span');
        codeEl.className = 'dcoded-promo__eligible-code';
        codeEl.textContent = code;
        info.appendChild(codeEl);

        if (desc) {
          const descEl = document.createElement('span');
          descEl.className = 'dcoded-promo__eligible-desc';
          descEl.textContent = desc;
          info.appendChild(descEl);
        }

        const tapBtn = document.createElement('button');
        tapBtn.type = 'button';
        tapBtn.className = 'dcoded-promo__tap-btn';
        tapBtn.dataset.tapCode = code;
        tapBtn.textContent = 'TAP TO USE';

        li.appendChild(info);
        li.appendChild(tapBtn);
        list.appendChild(li);
      });

      section.hidden = list.children.length === 0;
    }

    _ensureSheet() {
      this._reclaimSheetNodes();

      if (!this._overlay?.isConnected || !this._sheet?.isConnected) {
        this._resetSheetRefs();
        this._reclaimSheetNodes();
      }

      if (!this._overlay || !this._sheet) return;

      if (this._overlay.parentElement !== document.body) {
        document.body.appendChild(this._overlay);
      }
      if (this._sheet.parentElement !== document.body) {
        document.body.appendChild(this._sheet);
      }

      this._bindPortaledNodes(this._overlay, this._sheet);
      this._portaled = true;
    }

    _openSheet() {
      const drawer = this._getCartDrawer();
      if (drawer && !drawer.open) return;

      this._ensureSheet();
      if (!this._sheet || !this._overlay) return;

      activePromo = this;
      this._renderEligibleCodes();
      this._positionInDrawer();
      this._bindReposition();

      this._sheet.classList.add('dcoded-promo__sheet--open');
      this._overlay.classList.add('dcoded-promo__overlay--visible');

      const input = this._sheet.querySelector('.dcoded-promo__input');
      const error = this._sheet.querySelector('[data-promo-error]');
      if (input) {
        input.value = '';
        setTimeout(() => {
          this._positionInDrawer();
          input.focus();
        }, 300);
      }
      if (error) error.textContent = '';
    }

    _closeSheet() {
      this._unbindReposition();
      this._overlay?.classList.remove('dcoded-promo__overlay--visible');
      this._sheet?.classList.remove('dcoded-promo__sheet--open');
      this._restoreSheetToHost();

      if (!this._overlay?.isConnected && !this._sheet?.isConnected) {
        this._resetSheetRefs();
      }

      if (activePromo === this) activePromo = null;
    }

    _tapToUse(btn) {
      const code = btn.dataset.tapCode || '';
      if (!code) return;
      btn.classList.add('dcoded-promo__tap-btn--pulse');
      this._applyCode(code);
    }

    _applyFromInput() {
      const input = this._sheet?.querySelector('.dcoded-promo__input');
      const error = this._sheet?.querySelector('[data-promo-error]');
      if (!input) return;
      const code = input.value.trim();
      if (!code) return;
      if (error) error.textContent = '';
      this._applyCode(code);
    }

    _getSheetEl() {
      if (this._sheet?.isConnected) return this._sheet;
      return document.querySelector('[data-promo-sheet]');
    }

    _getErrorEl() {
      return this._getSheetEl()?.querySelector('[data-promo-error]') || null;
    }

    _getCartUpdateUrl() {
      if (typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root) {
        return Shopify.routes.root + 'cart/update.js';
      }
      return '/cart/update.js';
    }

    static _getDrawerSectionId() {
      const drawer = document.getElementById('cart-drawer') || document.querySelector('cart-drawer');
      if (drawer?.dataset?.sectionId) return drawer.dataset.sectionId;

      const section = document.querySelector('.shopify-section[id*="cart-drawer"]');
      if (section?.id) return section.id.replace('shopify-section-', '');

      const promo = document.querySelector('dcoded-cart-promo[data-section-id]');
      return promo?.dataset?.sectionId || '';
    }

    _getSectionIds() {
      const sectionId = DcodedCartPromo._getDrawerSectionId();
      const ids = sectionId ? [sectionId] : [];

      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: ids }
      }));
      return ids;
    }

    _isCodeApplied(cart, code) {
      const codeLower = normalizeCode(code);
      if (!codeLower) return false;

      if (cart.discount_codes?.some((entry) => normalizeCode(entry.code) === codeLower && entry.applicable)) {
        return true;
      }

      if (cart.cart_level_discount_applications?.some((entry) => normalizeCode(entry.title) === codeLower)) {
        return true;
      }

      return cart.items?.some((item) =>
        item.line_level_discount_allocations?.some(
          (alloc) => normalizeCode(alloc.discount_application?.title) === codeLower
        )
      );
    }

    _getDiscountAmount(cart, code) {
      const codeLower = normalizeCode(code);
      let amount = 0;

      cart.cart_level_discount_applications?.forEach((entry) => {
        if (normalizeCode(entry.title) === codeLower) {
          amount += entry.total_allocated_amount || 0;
        }
      });

      cart.items?.forEach((item) => {
        item.line_level_discount_allocations?.forEach((alloc) => {
          if (normalizeCode(alloc.discount_application?.title) === codeLower) {
            amount += alloc.amount || 0;
          }
        });
      });

      if (!amount && cart.total_discount > 0) {
        amount = cart.total_discount;
      }

      return amount;
    }

    _renderAppliedState(code, amount) {
      const inline = this.querySelector('.dcoded-promo__inline');
      if (!inline) return;

      const displayCode = code.toUpperCase();
      const displayAmount = formatMoney(amount || 0);

      this.dataset.appliedCode = displayCode;
      this.dataset.appliedAmount = String(amount || 0);

      inline.innerHTML = `
        <div class="dcoded-promo__trigger dcoded-promo__trigger--applied">
          <span class="dcoded-promo__trigger-left">
            <svg class="dcoded-promo__tag-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
            <span class="dcoded-promo__applied-code">${displayCode}</span>
            <span class="dcoded-promo__applied-amount">-${displayAmount}</span>
          </span>
          <span class="dcoded-promo__trigger-right">
            <button type="button" class="dcoded-promo__change-btn" data-promo-change>Change</button>
            <button type="button" class="dcoded-promo__remove-btn" data-promo-remove aria-label="Remove promo code">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/></svg>
            </button>
          </span>
        </div>
        <div class="dcoded-promo__confetti-wrap" data-promo-confetti></div>
      `;

      const defaultDiscounts = this.closest('.cart-drawer__summary')?.querySelector('.cart-drawer__default-discounts');
      if (defaultDiscounts) defaultDiscounts.hidden = true;

    }

    _updateCheckoutPrice(totalPrice) {
      const checkoutPrice = document.querySelector('.cart-drawer__button-price');
      if (checkoutPrice && totalPrice != null) {
        checkoutPrice.textContent = formatMoney(totalPrice);
      }
    }

    _setApplyLoading(isLoading) {
      const sheet = this._getSheetEl();
      sheet?.querySelector('[data-promo-apply]')?.toggleAttribute('disabled', isLoading);
      sheet?.querySelectorAll('[data-tap-code]').forEach((btn) => {
        if (isLoading) {
          btn.classList.add('dcoded-promo__tap-btn--loading');
          btn.textContent = 'APPLYING...';
        } else {
          btn.classList.remove('dcoded-promo__tap-btn--pulse', 'dcoded-promo__tap-btn--loading');
          btn.textContent = 'TAP TO USE';
        }
      });
    }

    async _applyCode(code) {
      const error = this._getErrorEl();
      const trimmedCode = (code || '').trim();
      if (!trimmedCode) return;

      this._setApplyLoading(true);
      if (error) error.textContent = '';

      try {
        const existingCodes = this._getExistingCodes();
        if (existingCodes.map(normalizeCode).includes(normalizeCode(trimmedCode))) {
          if (error) error.textContent = 'This code is already applied.';
          return;
        }

        const allCodes = [...existingCodes, trimmedCode].join(',');
        const sectionIds = this._getSectionIds();
        const res = await fetch(this._getCartUpdateUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            discount: allCodes,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname
          })
        });

        const data = await res.json();

        if (!res.ok) {
          if (error) error.textContent = data.description || data.message || 'Could not apply this code. Please try again.';
          return;
        }

        const rejectedCode = data.discount_codes?.find(
          (entry) => normalizeCode(entry.code) === normalizeCode(trimmedCode) && entry.applicable === false
        );

        if (rejectedCode) {
          if (error) {
            error.textContent =
              'This code is not valid for your cart. It may be for first-time customers only, or may not work with your current items.';
          }
          return;
        }

        if (!this._isCodeApplied(data, trimmedCode)) {
          if (error) {
            error.textContent =
              'This code could not be applied. It may be for first-time customers only, or may not work with your current items.';
          }
          return;
        }

        const appliedAmount = this._getDiscountAmount(data, trimmedCode);
        const appliedCode = data.discount_codes?.find(
          (entry) => normalizeCode(entry.code) === normalizeCode(trimmedCode) && entry.applicable
        )?.code || trimmedCode.toUpperCase();

        if (this._shouldAnimate()) {
          DcodedCartPromo._markJustApplied();
        }

        this._renderAppliedState(appliedCode, appliedAmount);
        this._updateCheckoutPrice(data.total_price);
        this._closeSheet();
        DcodedCartPromo._refreshCartSections(data);
      } catch (err) {
        if (error) error.textContent = 'Something went wrong. Please try again.';
      } finally {
        this._setApplyLoading(false);
      }
    }

    async _removeCode() {
      const currentCode = this.dataset.appliedCode;
      if (!currentCode) return;

      const existingCodes = this._getExistingCodes();
      const idx = existingCodes.findIndex((c) => c.toLowerCase() === currentCode.toLowerCase());
      if (idx !== -1) existingCodes.splice(idx, 1);

      try {
        const res = await fetch(this._getCartUpdateUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            discount: existingCodes.join(','),
            sections: this._getSectionIds().join(','),
            sections_url: window.location.pathname
          })
        });

        const data = await res.json();
        DcodedCartPromo._refreshCartSections(data);
      } catch (err) {
        console.error('Failed to remove promo code', err);
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    }

    _getExistingCodes() {
      const codes = [];
      document.querySelectorAll('dcoded-cart-promo[data-applied-code]').forEach((el) => {
        if (el.dataset.appliedCode) codes.push(el.dataset.appliedCode);
      });
      return codes;
    }

    static _refreshCartSections(data) {
      const cartDrawer = document.getElementById('cart-drawer') || document.querySelector('cart-drawer');
      const drawerWasOpen = Boolean(cartDrawer?.open);
      const sectionId = DcodedCartPromo._getDrawerSectionId();
      const hasSections = Boolean(sectionId && data.sections && data.sections[sectionId]);

      if (hasSections) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'promo:update',
            cart: data
          }
        }));
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));
      }

      if (drawerWasOpen && cartDrawer && !cartDrawer.open) {
        cartDrawer.show();
      }

      document.dispatchEvent(new CustomEvent('dcoded-cart-drawer-refreshed'));
    }

    _resetTapButtons() {
      this._setApplyLoading(false);
    }

    _burstConfetti(wrap) {
      const colors = ['#22c55e', '#4ade80', '#86efac', '#bbf7d0', '#dcfce7', '#16a34a', '#15803d', '#a7f3d0'];
      const shapes = ['circle', 'heart', 'rect'];

      for (let i = 0; i < 35; i++) {
        const piece = document.createElement('span');
        piece.classList.add('confetti-piece');

        const shape = shapes[Math.floor(Math.random() * shapes.length)];
        const color = colors[Math.floor(Math.random() * colors.length)];
        const left = Math.random() * 100;
        const delay = Math.random() * 0.5;
        const rotate = (Math.random() - 0.5) * 720;
        const size = 5 + Math.random() * 6;

        piece.style.left = left + '%';
        piece.style.animationDelay = delay + 's';
        piece.style.setProperty('--confetti-rotate', rotate + 'deg');

        if (shape === 'circle') {
          piece.style.width = size + 'px';
          piece.style.height = size + 'px';
          piece.style.borderRadius = '50%';
          piece.style.background = color;
        } else if (shape === 'heart') {
          piece.innerHTML = '<svg width="' + size + '" height="' + size + '" viewBox="0 0 10 10"><path d="M5 9 C3 7,0 5,0 3 A2 2,0,0,1,5 3 A2 2,0,0,1,10 3 C10 5,7 7,5 9Z" fill="' + color + '"/></svg>';
        } else {
          piece.style.width = size * 0.6 + 'px';
          piece.style.height = size + 'px';
          piece.style.background = color;
          piece.style.borderRadius = '1px';
        }

        wrap.appendChild(piece);
      }

      setTimeout(() => {
        wrap.querySelectorAll('.confetti-piece').forEach((p) => p.remove());
      }, 2500);
    }
  }

  if (!customElements.get('dcoded-cart-promo')) {
    customElements.define('dcoded-cart-promo', DcodedCartPromo);
  }

  const getPromoFromPortaled = (node) => {
    const overlay = node?.closest?.('[data-promo-overlay]');
    const sheet = node?.closest?.('[data-promo-sheet]');
    return overlay?._dcodedPromo || sheet?._dcodedPromo || activePromo || getActivePromo();
  };

  document.addEventListener('click', (e) => {
    const promo = getPromoFromNode(e.target);
    if (!promo) {
      const portaledPromo = getPromoFromPortaled(e.target);

      if (e.target.closest('[data-promo-overlay]')) {
        e.preventDefault();
        e.stopPropagation();
        portaledPromo?._closeSheet();
        return;
      }
      if (e.target.closest('[data-promo-close]')) {
        e.preventDefault();
        e.stopPropagation();
        portaledPromo?._closeSheet();
        return;
      }
      if (e.target.closest('[data-tap-code]')) {
        e.preventDefault();
        e.stopPropagation();
        portaledPromo?._tapToUse(e.target.closest('[data-tap-code]'));
        return;
      }
      if (e.target.closest('[data-promo-apply]')) {
        e.preventDefault();
        e.stopPropagation();
        portaledPromo?._applyFromInput();
        return;
      }
      return;
    }

    if (e.target.closest('[data-promo-remove]')) {
      e.preventDefault();
      e.stopPropagation();
      promo._removeCode();
      return;
    }

    if (e.target.closest('[data-promo-change]') || e.target.closest('[data-promo-trigger]')) {
      e.preventDefault();
      e.stopPropagation();
      promo._openSheet();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    const input = e.target.closest('.dcoded-promo__input');
    if (!input) return;
    e.preventDefault();
    const promo = getPromoFromNode(input) || getPromoFromPortaled(input);
    promo?._applyFromInput();
  });

})();

